import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface RedemptionRequest {
  token: string;
  userId: string;
}

export interface RedemptionResult {
  status: 'redeemed' | 'already_redeemed';
  userId: string;
}

export class RedemptionError extends Error {
  constructor(
    public code: 'invalid' | 'network' | 'authentication',
    message: string
  ) {
    super(message);
    this.name = 'RedemptionError';
  }
}

export const redeemToken = async ({
  token,
  userId,
}: RedemptionRequest): Promise<RedemptionResult> => {
  if (getAuth().currentUser?.uid !== userId) {
    throw new RedemptionError('authentication', 'Sign in to the selected account again');
  }

  const functions = getFunctions();
  const consumeToken = httpsCallable(functions, 'consumeToken', { timeout: 20_000 });

  try {
    await consumeToken(token);
    return { status: 'redeemed', userId };
  } catch (error: unknown) {
    // Handle Firebase Functions errors
    const code = (error as { code?: string })?.code;
    const message = (error as { message?: string })?.message || 'An error occurred';

    if (code === 'functions/already-exists') {
      // The backend records redemption per account. Tokens can grant different
      // benefits, so repeat use confirms redemption without inferring access.
      return { status: 'already_redeemed', userId };
    }

    if (code === 'functions/invalid-argument') {
      throw new RedemptionError('invalid', message);
    }

    if (code === 'functions/permission-denied' || code === 'functions/unauthenticated') {
      throw new RedemptionError('authentication', 'Authentication required');
    }

    throw new RedemptionError('network', message);
  }
};
